import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { saveFacturaEmitida } from '../../../../../firebase/facturacionUtils';
import { useFacturacionData } from '../../../../../context/FacturacionContext';
import { sanitizeAmount, formatNumberToInput } from '../../helpers';

const getFechaHoy = () => new Date().toISOString().substring(0, 10);
const getPeriodoActual = () => `${String(new Date().getMonth() + 1).padStart(2, '0')}/${new Date().getFullYear()}`;

export const calcularPeriodo = (fechaString) => {
    if (!fechaString) return '';
    const dateObj = new Date(fechaString);
    if (isNaN(dateObj.getTime())) return '';
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const y = dateObj.getFullYear();
    return `${m}/${y}`;
};

const INITIAL_FORM_DATA = {
    fechaIngreso: getFechaHoy(),
    fechaEmision: getFechaHoy(),
    periodo: getPeriodoActual(),
    tipoComprobante: '',
    numeroFactura: '',
    clienteId: '',
    clienteNombre: '',
    areaId: '',
    areaNombre: '',
    conceptoId: '',
    conceptoNombre: '',
    moneda: 'ARS',
    montoNeto: '',
    montoTotalInput: '',
    tasaIva: 21,
    esExentoIva: false, // 🟢 Check para Facturas B exentas/sin IVA
    montoIngresoBanco: '',
    formaPago: '',
    cuentaId: '',
    cuentaNombre: '',
    estatus: 'Pendiente de Cobro',
    fechaIngresoBanco: ''
};

const INITIAL_RETENCION_INPUT = { tipo: '', monto: '' };

export const useModalFacturaEmitida = ({ show, onClose, onSuccess, initialData = null, maestrosProp = {} }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { facturacionMasters } = useFacturacionData();
    
    const clientes = facturacionMasters?.clientes?.length > 0 ? facturacionMasters.clientes : (maestrosProp.clientes || []);
    const cuentas = facturacionMasters?.cuentas?.length > 0 ? facturacionMasters.cuentas : (maestrosProp.cuentas || []);
    const areas = facturacionMasters?.areas?.length > 0 ? facturacionMasters.areas : (maestrosProp.areas || []);
    const conceptosFacturas = facturacionMasters?.conceptosFacturas?.length > 0 ? facturacionMasters.conceptosFacturas : (maestrosProp.conceptosFacturas || maestrosProp.conceptos || []);

    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [retencionesList, setRetencionesList] = useState([]);
    const [retencionInput, setRetencionInput] = useState(INITIAL_RETENCION_INPUT);

    const prevShowRef = useRef(show);

    // Detección de Factura B / NC B
    const esFacturaB = useMemo(() => (
        formData.tipoComprobante === 'Factura B' || formData.tipoComprobante === 'Nota de Crédito B'
    ), [formData.tipoComprobante]);

    // Detección del tipo "Comprobante"
    const esComprobanteDirecto = useMemo(() => (
        formData.tipoComprobante === 'Comprobante'
    ), [formData.tipoComprobante]);

    // Indica si se ingresa Monto Total directamente (Factura B o Comprobante)
    const requiereMontoTotalDirecto = useMemo(() => (
        esFacturaB || esComprobanteDirecto
    ), [esFacturaB, esComprobanteDirecto]);

    useEffect(() => {
        if (show && (!prevShowRef.current || initialData?.id !== formData.id)) {
            if (initialData) {
                const esDirecto = initialData.tipoComprobante === 'Factura B' || 
                                  initialData.tipoComprobante === 'Nota de Crédito B' || 
                                  initialData.tipoComprobante === 'Comprobante';
                
                setFormData({
                    id: initialData.id,
                    fechaIngreso: initialData.fechaIngreso || getFechaHoy(),
                    fechaEmision: initialData.fechaEmision || getFechaHoy(),
                    periodo: initialData.periodo || getPeriodoActual(),
                    tipoComprobante: initialData.tipoComprobante || '',
                    numeroFactura: initialData.numeroFactura || '',
                    clienteId: initialData.clienteId || '',
                    clienteNombre: initialData.clienteNombre || '',
                    areaId: initialData.areaId || '',
                    areaNombre: initialData.areaNombre || '',
                    conceptoId: initialData.conceptoId || initialData.conceptoIngresoId || '',
                    conceptoNombre: initialData.conceptoNombre || initialData.conceptoIngreso || '',
                    moneda: initialData.moneda || 'ARS',
                    montoNeto: formatNumberToInput(initialData.montoNeto ?? initialData.precioNeto),
                    montoTotalInput: esDirecto ? formatNumberToInput(initialData.montoTotal ?? initialData.precioFinal) : '',
                    tasaIva: initialData.tasaIva !== undefined ? initialData.tasaIva : 21,
                    esExentoIva: initialData.esExentoIva ?? (initialData.tasaIva === 0),
                    montoIngresoBanco: formatNumberToInput(initialData.montoIngresoBanco),
                    formaPago: initialData.formaPago || '',
                    cuentaId: initialData.cuentaId || '',
                    cuentaNombre: initialData.cuentaNombre || '',
                    estatus: initialData.estatus || 'Pendiente de Cobro',
                    fechaIngresoBanco: initialData.fechaIngresoBanco || ''
                });

                if (Array.isArray(initialData.retencionesList) && initialData.retencionesList.length > 0) {
                    setRetencionesList(initialData.retencionesList);
                } else if (sanitizeAmount(initialData.retencionesTotales) > 0) {
                    setRetencionesList([{
                        tipo: 'Otras Retenciones',
                        monto: sanitizeAmount(initialData.retencionesTotales)
                    }]);
                } else {
                    setRetencionesList([]);
                }
            } else {
                setFormData(INITIAL_FORM_DATA);
                setRetencionesList([]);
            }
            setRetencionInput(INITIAL_RETENCION_INPUT);
            setError(null);
        }
        prevShowRef.current = show;
    }, [initialData, show]);

    const retencionesTotalesCalculadas = useMemo(() => {
        return retencionesList.reduce((acc, curr) => acc + (parseFloat(curr.monto) || 0), 0);
    }, [retencionesList]);

    // CÁLCULOS IMPOSITIVOS Y DESGLOSE
    const calculos = useMemo(() => {
        let neto = 0;
        let montoIva = 0;
        let montoTotal = 0;
        let tasa = parseFloat(formData.tasaIva) || 0;

        const esExento = !!formData.esExentoIva;

        if (esComprobanteDirecto) {
            // CASO "COMPROBANTE": Documento interno sin IVA
            montoTotal = sanitizeAmount(formData.montoTotalInput);
            neto = montoTotal;
            montoIva = 0;
            tasa = 0;
        } else if (esFacturaB) {
            montoTotal = sanitizeAmount(formData.montoTotalInput);

            if (esExento) {
                // CASO FACTURA B EXENTA / SIN IVA
                neto = montoTotal;
                montoIva = 0;
                tasa = 0;
            } else {
                // CASO FACTURA B ESTÁNDAR (Desglosa IVA 21%)
                tasa = 21;
                if (montoTotal > 0) {
                    neto = Math.round((montoTotal / 1.21) * 100) / 100;
                    montoIva = Math.round((montoTotal - neto) * 100) / 100;
                }
            }
        } else {
            // CASO GENERAL (Factura A, C, etc.)
            neto = sanitizeAmount(formData.montoNeto);
            montoIva = Math.round((neto * (tasa / 100)) * 100) / 100;
            montoTotal = Math.round((neto + montoIva) * 100) / 100;
        }

        const cobroEsperadoBanco = Math.round((montoTotal - retencionesTotalesCalculadas) * 100) / 100;
        const ingresoRealBanco = sanitizeAmount(formData.montoIngresoBanco);

        const diferenciaCobro = ingresoRealBanco > 0 
            ? Math.round(((ingresoRealBanco + retencionesTotalesCalculadas) - montoTotal) * 100) / 100 
            : 0;

        const monedaSimbolo = formData.moneda === 'USD' ? 'USD' : 'ARS';

        return {
            neto,
            tasa,
            montoIva,
            montoTotal,
            esExento,
            retencionesTotales: retencionesTotalesCalculadas,
            cobroEsperadoBanco,
            ingresoRealBanco,
            diferenciaCobro,
            monedaSimbolo
        };
    }, [
        formData.montoNeto, 
        formData.montoTotalInput, 
        formData.tasaIva, 
        formData.esExentoIva, 
        formData.montoIngresoBanco, 
        formData.moneda, 
        esFacturaB, 
        esComprobanteDirecto, 
        retencionesTotalesCalculadas
    ]);

    const handleBlurCurrency = useCallback((field) => {
        const value = formData[field];
        if (!value) return;
        const num = sanitizeAmount(value);
        if (num > 0) {
            setFormData(prev => ({
                ...prev,
                [field]: num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            }));
        }
    }, [formData]);

    const handleBlurMontoRetencion = useCallback(() => {
        if (!retencionInput.monto) return;
        const num = sanitizeAmount(retencionInput.monto);
        if (num > 0) {
            setRetencionInput(prev => ({
                ...prev,
                monto: num.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            }));
        }
    }, [retencionInput.monto]);

    const handleClienteChange = useCallback((e) => {
        const id = e.target.value;
        const found = clientes.find(c => c.id === id);
        setFormData(prev => ({
            ...prev,
            clienteId: id,
            clienteNombre: found ? (found.razonSocial || found.nombre) : ''
        }));
    }, [clientes]);

    const handleAreaChange = useCallback((e) => {
        const id = e.target.value;
        const found = areas.find(a => a.id === id);
        setFormData(prev => ({
            ...prev,
            areaId: id,
            areaNombre: found ? found.nombre : ''
        }));
    }, [areas]);

    const handleConceptoChange = useCallback((e) => {
        const id = e.target.value;
        const found = conceptosFacturas.find(c => c.id === id);
        setFormData(prev => ({
            ...prev,
            conceptoId: id,
            conceptoNombre: found ? found.nombre : ''
        }));
    }, [conceptosFacturas]);

    const handleCuentaChange = useCallback((e) => {
        const id = e.target.value;
        const found = cuentas.find(c => c.id === id);
        const nombreCompleto = found 
            ? (found.nombreDisplay || found.nombre || `${found.banco} • ${found.titular || ''} (${found.tipoCuenta || ''})`)
            : '';

        setFormData(prev => ({
            ...prev,
            cuentaId: id,
            cuentaNombre: nombreCompleto
        }));
    }, [cuentas]);

    const handleAddRetencion = useCallback(() => {
        if (!retencionInput.tipo) return setError("Seleccione un tipo de retención.");
        const numMonto = sanitizeAmount(retencionInput.monto);
        if (numMonto <= 0) return setError("El monto de la retención debe ser mayor a 0.");

        setRetencionesList(prev => [...prev, { tipo: retencionInput.tipo, monto: numMonto }]);
        setRetencionInput(INITIAL_RETENCION_INPUT);
        setError(null);
    }, [retencionInput]);

    const handleRemoveRetencion = useCallback((index) => {
        setRetencionesList(prev => prev.filter((_, idx) => idx !== index));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!formData.clienteId && !formData.clienteNombre) return setError("Debe seleccionar un Cliente obligatoriamente.");
        if (calculos.montoTotal <= 0) return setError("El Monto Total de la factura debe ser mayor a 0.");

        const montoIngresoReal = sanitizeAmount(formData.montoIngresoBanco);

        setLoading(true);
        try {
            const { montoTotalInput, ...formDataLimpio } = formData;

            const payload = {
                ...formDataLimpio,
                moneda: formData.moneda,
                montoNeto: calculos.neto,
                tasaIva: calculos.tasa,
                montoIva: calculos.montoIva,
                montoTotal: calculos.montoTotal,
                esExentoIva: !!formData.esExentoIva,
                retencionesTotales: calculos.retencionesTotales,
                retencionesList,
                montoIngresoBanco: montoIngresoReal,
                cobroEsperadoBanco: calculos.cobroEsperadoBanco,
                diferenciaCobro: calculos.diferenciaCobro
            };

            await saveFacturaEmitida(payload, initialData?.id || null);
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            console.error("Error al guardar factura emitida:", err);
            setError("Error técnico al intentar registrar la factura.");
        } finally {
            setLoading(false);
        }
    };

    return {
        formData, setFormData,
        retencionesList,
        retencionInput, setRetencionInput,
        loading, error,
        esFacturaB,
        esComprobanteDirecto,
        requiereMontoTotalDirecto,
        calculos,
        clientes, cuentas, areas, conceptosFacturas,
        handleBlurCurrency, handleBlurMontoRetencion,
        handleClienteChange, handleAreaChange, handleConceptoChange, handleCuentaChange,
        handleAddRetencion, handleRemoveRetencion,
        handleSubmit
    };
};